import React from 'react';
import { Divider, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { SyncProblem as UnsyncedIcon } from '@mui/icons-material';
import { surveyApi } from '../../services/api';
import { useSync } from '../../context/SyncContext';
import { useNetwork } from '../../context/NetworkContext';
import { pluralize } from '../../utils/formatters';
import DownloadMenu from './DownloadMenu';

/**
 * "Export" button with a menu: the data as an Excel workbook (one sheet per
 * record type) or one record type as CSV. `surveyIds` limits the export to
 * those surveys (omit for all); `scope` names the file, e.g. "survey-3".
 * Exports are made on the server, so the button is hidden offline, like Import
 * and Share.
 */
const ExportMenu = ({ surveyIds, scope = 'all', disabled = false, label = 'Export' }) => {
  const { pending } = useSync();
  const { offline } = useNetwork();
  if (offline) return null;

  const fetchFile = async (format, table) => {
    const date = new Date().toISOString().slice(0, 10);
    return {
      blob: await surveyApi.exportData({ format, table, surveyIds }),
      filename: format === 'xlsx' ? `mangrove_export_${scope}_${date}.xlsx` : `mangrove_export_${scope}_${table}_${date}.csv`,
    };
  };

  const note = pending > 0 && [
    <ListItem key="unsynced" sx={{ py: 1, maxWidth: 320, alignItems: 'flex-start' }}>
      <ListItemIcon sx={{ color: 'warning.main', mt: 0.25 }}><UnsyncedIcon fontSize="small" /></ListItemIcon>
      <ListItemText
        primary={`${pluralize(pending, 'change')} on this device ${pending === 1 ? "hasn't" : "haven't"} synced yet and won't be in the export.`}
        primaryTypographyProps={{ variant: 'body2', color: 'warning.dark' }}
      />
    </ListItem>,
    <Divider key="unsynced-divider" />,
  ];

  return (
    <DownloadMenu
      id="export-menu"
      label={label}
      fetchFile={fetchFile}
      excelHint="Every record type, one sheet each"
      note={note}
      failure="Could not download the export."
      disabled={disabled}
      buttonProps={{ variant: 'outlined' }}
    />
  );
};

export default ExportMenu;
